<script lang="ts">
	import SortItems from '$lib/components/common/SortItems.svelte'
	import CategoryHero from '$lib/components/category/CategoryHero.svelte'
	import CategoryTitle from '$lib/components/category/CategoryTitle.svelte'
	import Filters from '$lib/components/category/Filters.svelte'
	import CategoryProducts from '$lib/components/category/CategoryProducts.svelte'
	import StructuredData from '$lib/components/common/StructuredData.svelte'
	import IntersectionObserver from 'svelte-intersection-observer'
	import { getMoreCategoryProducts, loadMoreProducts } from '$lib/js/products'
	import { storeCurrentPage, storeMaxPrice, storeNextPageURL } from '$lib/js/stores'
	import { getIntSearchParam, is_set_array, orderCategoryResults, setSearchParam } from '$lib/js/helpers'
	import { getMerchantSchema } from '$lib/js/structured_data'
	import { registerViewItemListEvent } from '$lib/js/ga4/events'
	import { getBrandsParam } from '$lib/js/helpers/handlers'
	import { PUBLIC_SITE_URL } from '$env/static/public'
	import { onMount } from 'svelte'
	import { page } from '$app/stores'
	import { t } from 'svelte-i18n-lingui'
	import type { BaseProduct, FullProduct, ProductQueryOptions } from '$lib/js/elasticsearch/types'
	import { getContextScope } from '$lib/js/helpers/i18n'

	export let category: { name: string; id: any }

	const scope = getContextScope(),
		endpoint = 'category'

	let products: (BaseProduct | FullProduct)[] = $page.data.products
	let top_prods: HTMLElement
	let elementOnce: HTMLElement | undefined // Infinite scroll
	let allResults = false // Get results page number

	/* Order & filter products */
	let options: ProductQueryOptions = {
		cat_id: category.id,
		start: getIntSearchParam($page.url, 'p', 1),
		sort: orderCategoryResults($page.url.searchParams.get('sort') || 'rating'),
		min_price: getIntSearchParam($page.url, 'minPrice', 0),
		max_price: getIntSearchParam($page.url, 'maxPrice', 200000),
		brand_ids: getBrandsParam($page.url, 'brands', $page.data.brands),
	}

	onMount(() => {
		// TODO[i18n]: this 1.21 does not work for France
		$storeMaxPrice = parseInt(($page.data.max_price.value * 1.21 + 1).toFixed(0))

		if (getIntSearchParam($page.url, 'p', 1) > 1) {
			setTimeout(() => {
				top_prods.scrollIntoView()
			}, 500)
		}
		registerViewItemListEvent($page.data.category, $page.data.products)
	})
</script>

<svelte:head>
	<link rel="canonical" href="{PUBLIC_SITE_URL}/{scope}/c/{$page.params?.cat_url}" />
</svelte:head>

{#if is_set_array(products)}
	{#each products as product}
		<StructuredData schema={getMerchantSchema(product, {scope: scope})} />
	{/each}
{/if}

<CategoryHero {category} />
<!-- <TopBannerPromo /> -->

<div bind:this={top_prods} class="container category__wrapper">
	<CategoryTitle {category} />

	<div class="category__content">
		<div class="category__actions">
			<div class="category__actions__block">
				<Filters bind:products bind:options {endpoint} />
			</div>
			<div class="sort-items__mobile">
				<SortItems bind:products bind:options {endpoint} />
			</div>
		</div>

		<div class="category__products">
			<div class="sort-items__desktop">
				<SortItems bind:products bind:options {endpoint} />
			</div>
			<CategoryProducts bind:products bind:allResults />
		</div>
	</div>

	<IntersectionObserver
		element={elementOnce}
		rootMargin="800px"
		on:observe={async (e) => {
			if (e.detail.isIntersecting === true) {
				let resp

				try {
					resp = await getMoreCategoryProducts(options, {scope: scope})
				} catch (err) {
					console.error('Error trying to load more products in category:', err)
				}

				if(!resp || resp?.length === 0) {
					allResults = true
				} else {
					allResults = false
					products = loadMoreProducts(products, resp)
					setSearchParam('p', $storeCurrentPage.toString())
					resp = undefined
				}
			}
		}}
	>
		<div bind:this={elementOnce} />
	</IntersectionObserver>

	{#if $page.data?.description}
		<div class="category__description">{@html $page.data.description.description}</div>
	{/if}

	<a href={$storeNextPageURL} style="visibility: hidden">{$t`Page ${$storeCurrentPage + 1}`}</a>
</div>

<style lang="scss">
  .sort-items {
    &__mobile {
      @media (min-width: 1025px) {
        display: none;
      }

      @media (max-width: 1024px) {
        margin: 12px 20px 12px 12px;
      }
    }

    &__desktop {
      display: none;

      @media (min-width: 1025px) {
        display: block;
      }
    }
  }

  .category {
    &__actions {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;

      &__block {
        @media (max-width: 1024px) {
          flex-grow: 1;
        }
        @media (min-width: 1025px) {
          padding: 8px 20px;
          box-shadow: 0px 0px 20px 4px rgba(0, 0, 0, 0.1);
          border-radius: 8px;
        }
      }

      @media (max-width: 1024px) {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: #f0f0f0;
        z-index: 9;
        box-shadow: 0px 0px 20px 10px rgba(0, 0, 0, 0.12);
      }

      @media (min-width: 1025px) {
        position: sticky;
        top: 140px;
      }
    }

    &__wrapper {
      margin-top: 40px;
      margin-bottom: 60px;
    }

    &__content {
      @media (min-width: 1025px) {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
      }
    }

    &__products {
      @media (min-width: 1025px) {
        width: calc(100% - 330px);
      }
    }

    &__description {
      margin-top: 30px;
      max-width: 700px;

      @media (min-width: 1025px) {
        margin-top: 60px;
        margin-left: 330px;
      }
    }
  }
</style>
